import { useState } from 'react';
import cn from 'classnames';
import { IconButton, useDisclosure } from '@ds';
import { ConditionalRender, DeleteModal } from '@ui';
import { MdDelete, MdEdit } from 'react-icons/md';
import { ReleaseNote } from '@/api/apis/release-notes';
import { onActivateKeyDown } from '@/utils/helpers';
import { formatReleaseDate } from './utils';

type ReleaseNoteListItemProps = {
  release: ReleaseNote;
  isCurrent: boolean;
  isSelected: boolean;
  onSelect: (releaseId: string) => void;
  onEdit?: (release: ReleaseNote) => void;
  onDelete?: (release: ReleaseNote) => Promise<boolean>;
};

export const ReleaseNoteListItem = ({
  release,
  isCurrent,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}: ReleaseNoteListItemProps) => {
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    const isDeleted = await onDelete?.(release);
    setIsDeleting(false);

    if (isDeleted) {
      onDeleteClose();
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between px-4 py-3 m-0 text-base-10 text-sm transition-colors border-l-2',
        {
          'bg-white border-base-10': isSelected,
          'border-transparent hover:bg-base-80': !isSelected,
        },
      )}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={`Select release ${formatReleaseDate(release.releaseDate)}`}
        onClick={() => onSelect(release.id)}
        onKeyDown={onActivateKeyDown(() => onSelect(release.id))}
        className="flex-1 text-left cursor-pointer"
      >
        <span className="text-s-m">{formatReleaseDate(release.releaseDate)}</span>
        {isCurrent && <span className="ml-2 text-xs">(current)</span>}
      </div>
      <ConditionalRender show={Boolean(onEdit || onDelete)}>
        <div className="flex items-center gap-1 ml-auto">
          <ConditionalRender show={Boolean(onEdit)}>
            <IconButton
              size="sm"
              variant="light"
              aria-label="Edit release note"
              startContent={<MdEdit size={16} />}
              onPress={() => onEdit?.(release)}
            />
          </ConditionalRender>
          <ConditionalRender show={Boolean(onDelete)}>
            <IconButton
              size="sm"
              variant="light"
              aria-label="Delete release note"
              startContent={<MdDelete size={16} />}
              onPress={onDeleteOpen}
            />
          </ConditionalRender>
        </div>
      </ConditionalRender>

      <ConditionalRender show={Boolean(onDelete)}>
        <DeleteModal
          title="Delete Release Note?"
          description={`Are you sure you want to delete release ${release.version}?`}
          highlightedText={release.version}
          isOpen={isDeleteOpen}
          isLoading={isDeleting}
          onDelete={handleConfirmDelete}
          onClose={onDeleteClose}
        />
      </ConditionalRender>
    </div>
  );
};
